import React from 'react';
import { motion } from 'framer-motion';
import '../styles/EnergyInfrastructure.css';
import EnergyInvestmentChart from '../components/EnergyInvestmentChart';

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
};

const fadeUp = {
  hidden: { opacity: 0, filter: 'blur(8px)' },
  visible: { opacity: 1, filter: 'blur(0)', transition: { duration: 1 } }
};

const EnergyInfrastructure = () => (
  <div className="energyinfra-container">
    {/* Hero Section */}
    <motion.div
      className="energyinfra-hero"
      initial="hidden"
      animate="visible"
      variants={fadeUp}
    >
      <img
        src="../../Images/infrastructure.webp"
        alt="Floating Gas Unit"
      />
      <div className="energyinfra-hero-overlay">
        <div>
          <h1>Energy Infrastructure</h1>
          <h2 className="energyinfra-subtitle">Modular, Scalable, Smart Infrastructure for Africa</h2>
        </div>
      </div>
    </motion.div>
    
    {/* Feature Section */}
    <div className="container">
      <section className="energyinfra-feature-section">
        <div className="energyinfra-feature-grid">
          <motion.div
            className="energyinfra-text"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <h2>Smart Infrastructure for Nigeria’s Gas Future</h2>
            <p>
              To support Nigeria’s gas future, GGOEX is developing <strong>smart infrastructure</strong> designed to process, transport, and store natural gas and its derivatives.
            </p>
            <p>
              We deploy <strong>Floating Gas Processing Units (FGPUs)</strong>, which are modular and deploy 30% faster than traditional FPSO systems. Our pipeline integration model supports regional interconnectivity and cross-border delivery.
            </p>
            <p>
              In alignment with Nigeria’s Presidential CNG Initiative (Pi-CNG), GGOEX is building infrastructure that aligns with both local development and export ambitions.
            </p>
          </motion.div>

          <motion.div
            className="energyinfra-image"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <img
              src="../../Images/model-hyundai.jpg"
              alt="Floating Production Unit"
            />
          </motion.div>
        </div>
      </section>
      <EnergyInvestmentChart />

      {/* Table Section */}
      {/* <motion.section
        className="energyinfra-overview"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <h2>Infrastructure Highlights</h2>
        <div className="energyinfra-table-wrapper">
          <table className="energyinfra-table">
            <tbody>
              <tr><th>FGPU Capacity (per unit)</th><td>300 MMscfd</td></tr>
              <tr><th>Deployment Speed</th><td>30% faster than FPSOs</td></tr>
              <tr><th>Modular Pipeline Integration</th><td>Yes</td></tr>
              <tr><th>Regional Export Potential</th><td>Nigeria ↔ Ghana, Equatorial Guinea</td></tr>
              <tr><th>Technology Partners</th><td>Zentech, GSP Offshore</td></tr>
              <tr><th>CNG Support</th><td>Pi-CNG alignment, 2026-ready</td></tr>
            </tbody>
          </table>
        </div>
      </motion.section> */}

      {/* Highlight Section */}
      <motion.section
        className="energyinfra-highlight-section"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <blockquote>
          "Our infrastructure portfolio includes smart MOPU/FGPU architecture, modular pipelines, and CNG-ready assets to serve both domestic and export markets."
        </blockquote>
      </motion.section>
    </div>

    {/* Technology Integration and Deployment Strategy */}
    <section className="energyinfra-feature-section">
      <div className="container">
      <div className="energyinfra-feature-grid">
        <motion.div
          className="energyinfra-text"
          variants={fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <h2>Technology Integration and Deployment Strategy</h2>
          <p style={{ textAlign: 'justify' }}>
            GGOEX's gas infrastructure strategy will focus exclusively on gas infrastructure systems utilizing modular and mobile offshore technologies, including Mobile Offshore Production Units (MOPUs), Floating Gas Production Units (FGPUs), and associated processing systems. This strategic emphasis will align with West Africa's urgent need to monetize stranded gas reserves while addressing regional energy poverty and infrastructure gaps. The company will prioritize deployment of smart pipeline networks, integrated power systems, and cross-border connectivity solutions to support regional energy integration.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Mobile Offshore Production Units will be deployed as cost-effective, reusable platforms for gas production, processing, and early monetization in shallow and deep offshore fields. These units will provide rapid mobilization capabilities, enabling production start-up and relocation as fields mature or new opportunities arise, reducing capital costs and project timelines compared to conventional fixed platforms.
          </p>
          <p style={{ textAlign: 'justify' }}>
            FGPUs will offer processing capabilities for 200-300 MMscfd of raw gas, featuring integrated three-phase separators, glycol dehydration units, and metering systems within buoyant hulls.
          </p>
        </motion.div>
      </div>
      </div>
    </section>

    {/* Investment Outlook and Market Analysis */}
    <section className="energyinfra-feature-section energyinfra-alt-bg">
      <div className="container">
      <div className="energyinfra-feature-grid">
        <motion.div
          className="energyinfra-text"
          variants={fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <h2>Investment Outlook and Market Analysis</h2>
          <p style={{ textAlign: 'justify' }}>
            <strong>Regional Investment Distribution:</strong> West Africa will continue to dominate continental energy investment, contributing over 50% of Africa's total energy capital expenditure through 2030. The regional investment distribution reveals diverse opportunities across multiple countries, with Nigeria leading at $15 billion investment potential concentrated in gas monetization through NLNG Train-7 expansion and similar projects.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Mozambique leads with a $20 billion FLNG market, while Equatorial Guinea’s $8 billion Gas Mega Hub cements its role as a regional pipeline center. Senegal’s $12 billion Greater Tortue Ahmeyim project advances LNG exports, and Ghana’s $5 billion offshore drilling targets new exploration. Angola adds $10 billion deepwater expansion, totaling over $70 billion in West African energy investment. The offshore drilling sector is robust, with 46 rig years demand in 2025.
          </p>
          <p style={{ textAlign: 'justify' }}>
            GGOEX’s modular gas infrastructure—MOPUs and FGPUs—delivers 30% and 25% capital cost reductions respectively, with deployment times of 12 and 18 months versus 36 for traditional platforms, offering mobility and higher processing capacity for efficient field optimization.
          </p>
        </motion.div>
      </div>
      </div>
    </section>

    {/* Strategic Technical Partnership */}
    <section className="energyinfra-feature-section">
      <div className="container">
      <div className="energyinfra-feature-grid">
        <motion.div
          className="energyinfra-text"
          variants={fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <h2>Strategic Technical Partnership</h2>
          <p style={{ textAlign: 'justify' }}>
            GGOEX’s strategic partnership with Zentech Inc. delivers crucial technical expertise and proven execution capabilities in MOPU conversions. Zentech’s global track record includes successful MOPU projects like the Hibiscus Alpha conversion for BW Energy in West Africa and the Key Gibraltar conversion for HOEC in India, as well as ongoing work with New Fortress Energy in the Gulf of Mexico. This alliance ensures access to reliable, scalable solutions for offshore gas processing.
          </p>
        </motion.div>
      </div>
      </div>
    </section>
  </div>
);

export default EnergyInfrastructure;
